import React, { useState, useEffect } from "react";
import {
  Container,
  Tabs,
  Card,
  Group,
  Stack,
  Text,
  Button,
  TextInput,
  PasswordInput,
  Switch,
  Select,
  Modal,
  Badge,
  Table,
  Divider,
  Avatar,
  ActionIcon,
  CopyButton,
  Code,
  Image,
  Paper,
  Title,
  Grid,
  LoadingOverlay,
  Progress,
} from "@mantine/core";
import {
  IconUser,
  IconShield,
  IconSettings,
  IconActivity,
  IconCheck,
  IconX,
  IconCopy,
  IconDeviceMobile,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import {
  changePassword,
  enable2FA,
  getSecuritySettings,
  getUserActivity,
  getUserProfile,
  sendPhoneVerification,
  setup2FA,
  updateUserPreferences,
  updateUserProfile,
  verifyPhoneNumber,
} from "../misc/api";

const UserProfileScreen = () => {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [securitySettings, setSecuritySettings] = useState(null);
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [activity, setActivity] = useState([]);
  const [preferences, setPreferences] = useState({});

  // Modals
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [twoFactorModal, setTwoFactorModal] = useState(false);
  const [phoneVerificationModal, setPhoneVerificationModal] = useState(false);

  const profileForm = useForm({
    initialValues: {
      name: "",
      surname: "",
      phone: "",
    },
    validate: {
      name: (value) => (value.length < 2 ? "Name is too short" : null),
      surname: (value) => (value.length < 2 ? "Surname is too short" : null),
      phone: (value) =>
        value && !/^\+?[\d\s\-\(\)]{8,20}$/.test(value)
          ? "Invalid phone number"
          : null,
    },
  });

  const passwordForm = useForm({
    initialValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
    validate: {
      current_password: (value) =>
        value.length < 6 ? "Password is too short" : null,
      new_password: (value) =>
        value.length < 6 ? "Password must be at least 6 characters" : null,
      confirm_password: (value, values) =>
        value !== values.new_password ? "Passwords do not match" : null,
    },
  });

  const twoFactorForm = useForm({
    initialValues: {
      token: "",
    },
    validate: {
      token: (value) => (!/^\d{6}$/.test(value) ? "Invalid code format" : null),
    },
  });

  const phoneVerificationForm = useForm({
    initialValues: {
      code: "",
    },
    validate: {
      code: (value) => (!/^\d{6}$/.test(value) ? "Invalid code format" : null),
    },
  });

  useEffect(() => {
    loadProfile();
    loadSecuritySettings();
    loadActivity();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await getUserProfile();
      if (data.success) {
        setProfile(data.data);

        profileForm.setValues({
          name: data.data.name || "",
          surname: data.data.surname || "",
          phone: data.data.phone || "",
        });

        const parsed = data.data.preferences;
        setPreferences(parsed);
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to load profile",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSecuritySettings = async () => {
    try {
      const data = await getSecuritySettings();

      if (data.success) {
        setSecuritySettings(data.data);
      }
    } catch (error) {
      console.error("Failed to load security settings:", error);
    }
  };

  const loadActivity = async () => {
    try {
      const data = await getUserActivity();

      if (data.success) {
        setActivity(data.data);
      }
    } catch (error) {
      console.error("Failed to load activity:", error);
    }
  };

  const handleProfileUpdate = async (values) => {
    try {
      setLoading(true);
      const data = await updateUserProfile(values);

      if (data.success) {
        notifications.show({
          title: "Success",
          message: "Profile updated successfully",
          color: "green",
        });
        loadProfile();
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update profile",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (values) => {
    try {
      setLoading(true);
      const data = await changePassword(values);

      if (data.success) {
        notifications.show({
          title: "Success",
          message: "Password changed successfully",
          color: "green",
        });
        setChangePasswordModal(false);
        passwordForm.reset();
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to change password",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  // Two-Factor Authentication
  const handleSetup2FA = async () => {
    try {
      setLoading(true);
      const data = setup2FA();

      if (data.success) {
        setTwoFactorSetup(data.data);
        setTwoFactorModal(true);
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to setup 2FA",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async (values) => {
    try {
      setLoading(true);
      const data = await enable2FA(values);

      if (data.success) {
        notifications.show({
          title: "Success",
          message: "Two-factor authentication enabled",
          color: "green",
        });
        setTwoFactorModal(false);
        loadSecuritySettings();

        // Show backup codes
        modals.open({
          title: "Backup Codes",
          children: (
            <Stack>
              <Text size="sm">
                Save these backup codes in a safe place. You can use them to
                access your account if you lose your device.
              </Text>
              <Paper p="md" withBorder>
                {data.data.backupCodes?.map((code, index) => (
                  <Code key={index} block>
                    {code}
                  </Code>
                ))}
              </Paper>
              <Button
                onClick={() => {
                  const codes = data.data.backupCodes?.join("\n");
                  navigator.clipboard.writeText(codes);
                  notifications.show({
                    message: "Backup codes copied to clipboard",
                    color: "green",
                  });
                }}
              >
                Copy All Codes
              </Button>
            </Stack>
          ),
        });
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to enable 2FA",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = () => {
    modals.openConfirmModal({
      title: "Disable Two-Factor Authentication",
      children: (
        <Text size="sm">
          Are you sure you want to disable two-factor authentication? This will
          make your account less secure.
        </Text>
      ),
      labels: { confirm: "Disable", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        // Implementation for disable 2FA
        notifications.show({
          message: "2FA disabled",
          color: "orange",
        });
      },
    });
  };

  // Phone verification
  const handleSendPhoneVerification = async () => {
    try {
      setLoading(true);
      const data = await sendPhoneVerification();

      if (data.success) {
        notifications.show({
          title: "Success",
          message: "Verification code sent",
          color: "green",
        });
        setPhoneVerificationModal(true);
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to send verification code",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPhone = async (values) => {
    try {
      setLoading(true);
      const data = await verifyPhoneNumber(values);

      if (data.success) {
        notifications.show({
          title: "Success",
          message: "Phone number verified",
          color: "green",
        });
        setPhoneVerificationModal(false);
        loadProfile();
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to verify phone",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  // Preferences update
  const handlePreferencesUpdate = async (key, value) => {
    try {
      const newPreferences = { ...preferences, [key]: value };
      const data = await updateUserPreferences(newPreferences);

      if (data.success) {
        setPreferences(newPreferences);
        notifications.show({
          title: "Success",
          message: "Preferences updated",
          color: "green",
        });
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update preferences",
        color: "red",
      });
    }
  };

  // Helper function (assume getToken exists)
  const getToken = () => {
    return localStorage.getItem("token") || "";
  };

  if (!profile) {
    return (
      <Container size="lg" py="xl">
        <LoadingOverlay visible={true} />
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Title mb="xl">Account Settings</Title>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="profile" leftSection={<IconUser size="0.8rem" />}>
            Profile
          </Tabs.Tab>
          <Tabs.Tab value="security" leftSection={<IconShield size="0.8rem" />}>
            Security
          </Tabs.Tab>
          <Tabs.Tab
            value="preferences"
            leftSection={<IconSettings size="0.8rem" />}
          >
            Preferences
          </Tabs.Tab>
          {/* <Tabs.Tab
            value="activity"
            leftSection={<IconActivity size="0.8rem" />}
          >
            Activity
          </Tabs.Tab> */}
        </Tabs.List>

        {/* Profile Tab */}
        <Tabs.Panel value="profile" pt="xs">
          <Grid>
            <Grid.Col span={12}>
              <Card withBorder padding="lg">
                <Group justify="space-between" mb="md">
                  <Text weight={500} size="lg">
                    Profile Information
                  </Text>
                  <Avatar size="lg" radius="xl">
                    {profile.name?.[0]}
                    {profile.surname?.[0]}
                  </Avatar>
                </Group>
                <form onSubmit={profileForm.onSubmit(handleProfileUpdate)}>
                  <Stack spacing="md">
                    <Group grow>
                      <TextInput
                        label="First Name"
                        placeholder="Enter first name"
                        {...profileForm.getInputProps("name")}
                      />
                      <TextInput
                        label="Last Name"
                        placeholder="Enter last name"
                        {...profileForm.getInputProps("surname")}
                      />
                    </Group>

                    <TextInput
                      label="Email"
                      value={profile.email}
                      disabled
                      rightSection={
                        profile.email_verified ? (
                          <IconCheck color="green" size="1rem" />
                        ) : (
                          <IconX color="red" size="1rem" />
                        )
                      }
                    />

                    <Group>
                      <TextInput
                        label="Phone Number"
                        placeholder="Enter phone number"
                        {...profileForm.getInputProps("phone")}
                        style={{ flex: 1 }}
                        rightSection={
                          profile.phone_verified ? (
                            <IconCheck color="green" size="1rem" />
                          ) : (
                            <IconX color="red" size="1rem" />
                          )
                        }
                      />
                      {!profile.phone_verified && profile.phone && (
                        <Button
                          variant="outline"
                          size="sm"
                          mt="xl"
                          onClick={handleSendPhoneVerification}
                        >
                          Verify
                        </Button>
                      )}
                    </Group>

                    <Group justify="flex-end">
                      <Button type="submit" loading={loading}>
                        Update Profile
                      </Button>
                    </Group>
                  </Stack>
                </form>
              </Card>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        {/* Security Tab */}
        <Tabs.Panel value="security" pt="xs">
          <Stack spacing="md">
            {/* Password Section */}
            <Card withBorder padding="lg">
              <Group justify="space-between" mb="md">
                <div>
                  <Text weight={500} size="lg">
                    Password
                  </Text>
                  <Text size="sm" c="dimmed">
                    Last changed:{" "}
                    {securitySettings?.password_changed_at
                      ? new Date(
                          securitySettings.password_changed_at
                        ).toLocaleDateString()
                      : "Never"}
                  </Text>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setChangePasswordModal(true)}
                >
                  Change Password
                </Button>
              </Group>
            </Card>

            {/* Two-Factor Authentication */}
            <Card withBorder padding="lg">
              <Group justify="space-between" mb="md">
                <div>
                  <Text weight={500} size="lg">
                    Two-Factor Authentication
                  </Text>
                  <Text size="sm" c="dimmed">
                    Add an extra layer of security to your account
                  </Text>
                </div>
                <Badge
                  color={
                    securitySettings?.two_factor_enabled ? "green" : "gray"
                  }
                >
                  {securitySettings?.two_factor_enabled
                    ? "Enabled"
                    : "Disabled"}
                </Badge>
              </Group>

              {securitySettings?.two_factor_enabled ? (
                <Group>
                  <Button
                    variant="outline"
                    color="red"
                    onClick={handleDisable2FA}
                  >
                    Disable 2FA
                  </Button>
                </Group>
              ) : (
                <Group>
                  <Button onClick={handleSetup2FA}>Enable 2FA</Button>
                </Group>
              )}
            </Card>

            {/* Phone Verification */}
            <Card withBorder padding="lg">
              <Group justify="space-between" mb="md">
                <div>
                  <Text weight={500} size="lg">
                    Phone Verification
                  </Text>
                  <Text size="sm" c="dimmed">
                    Verify your phone number for SMS notifications
                  </Text>
                </div>
                <Badge color={profile.phone_verified ? "green" : "gray"}>
                  {profile.phone_verified ? "Verified" : "Not Verified"}
                </Badge>
              </Group>

              {!profile.phone_verified && profile.phone && (
                <Button
                  variant="outline"
                  onClick={handleSendPhoneVerification}
                  leftSection={<IconDeviceMobile size="1rem" />}
                >
                  Send Verification Code
                </Button>
              )}
            </Card>
          </Stack>
        </Tabs.Panel>

        {/* Preferences Tab */}
        <Tabs.Panel value="preferences" pt="xs">
          <Card withBorder padding="lg">
            <Text weight={500} size="lg" mb="md">
              Preferences
            </Text>

            <Stack spacing="md">
              <Group justify="space-between">
                <div>
                  <Text weight={500}>Email Notifications</Text>
                  <Text size="sm" c="dimmed">
                    Receive notifications via email
                  </Text>
                </div>
                <Switch
                  checked={preferences.email_notifications}
                  onChange={(event) =>
                    handlePreferencesUpdate(
                      "email_notifications",
                      event.currentTarget.checked
                    )
                  }
                />
              </Group>

              {/* <Group justify="space-between">
                <div>
                  <Text weight={500}>SMS Notifications</Text>
                  <Text size="sm" c="dimmed">
                    Receive notifications via SMS
                  </Text>
                </div>
                <Switch
                  checked={preferences.sms_notifications || false}
                  onChange={(event) =>
                    handlePreferencesUpdate(
                      "sms_notifications",
                      event.currentTarget.checked
                    )
                  }
                />
              </Group> */}

              <Divider />

              {/* <Select
                label="Language"
                value={preferences.language || "en"}
                onChange={(value) => handlePreferencesUpdate("language", value)}
                data={[
                  { value: "en", label: "English" },
                  { value: "lv", label: "Latviešu" },
                ]}
              /> */}

              {/* <Select
                label="Date Format"
                value={preferences.date_format || "DD/MM/YYYY"}
                onChange={(value) =>
                  handlePreferencesUpdate("date_format", value)
                }
                data={[
                  { value: "DD/MM/YYYY", label: "DD/MM/YYYY" },
                  { value: "MM/DD/YYYY", label: "MM/DD/YYYY" },
                  { value: "YYYY-MM-DD", label: "YYYY-MM-DD" },
                ]}
              />
              /> */}
            </Stack>
          </Card>
        </Tabs.Panel>

        {/* Activity Tab */}
        <Tabs.Panel value="activity" pt="xs">
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="md">
              <Text weight={500} size="lg">
                Recent Activity
              </Text>
            </Group>

            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Action</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>IP Address</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {activity.map((item, index) => (
                  <Table.Tr key={index}>
                    <Table.Td>{item.action}</Table.Td>
                    <Table.Td>
                      {new Date(item.created_at).toLocaleString()}
                    </Table.Td>
                    <Table.Td>{item.ip_address || "N/A"}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {activity.length === 0 && (
              <Text ta="center" c="dimmed" py="xl">
                No activity logged yet
              </Text>
            )}
          </Card>
        </Tabs.Panel>
      </Tabs>

      {/* Modals */}
      {/* Change Password Modal */}
      {/* <Modal
        opened={changePasswordModal}
        onClose={() => setChangePasswordModal(false)}
        title="Change Password"
      >
        <form onSubmit={passwordForm.onSubmit(handlePasswordChange)}>
          <Stack spacing="md">
            <PasswordInput
              label="Current Password"
              placeholder="Enter current password"
              {...passwordForm.getInputProps("current_password")}
            />
            <PasswordInput
              label="New Password"
              placeholder="Enter new password"
              {...passwordForm.getInputProps("new_password")}
            />
            <PasswordInput
              label="Confirm New Password"
              placeholder="Confirm new password"
              {...passwordForm.getInputProps("confirm_password")}
            />
            <Group justify="flex-end">
              <Button
                variant="outline"
                onClick={() => setChangePasswordModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" loading={loading}>
                Change Password
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal> */}
      {/* Change Password Modal */}
      <Modal
        opened={changePasswordModal}
        onClose={() => setChangePasswordModal(false)}
        title="Change Password"
      >
        <form onSubmit={passwordForm.onSubmit(handlePasswordChange)}>
          <Stack spacing="md">
            {/* Current password */}
            <PasswordInput
              label="Current Password"
              placeholder="Enter current password"
              {...passwordForm.getInputProps("current_password")}
              rightSection={
                passwordForm.values.current_password.length >= 6 ? (
                  <IconCheck size="1rem" color="green" />
                ) : null
              }
              error={passwordForm.errors.current_password}
            />

            {/* New password with strength bar */}
            <Stack spacing={4}>
              <PasswordInput
                label="New Password"
                placeholder="Enter new password"
                {...passwordForm.getInputProps("new_password")}
                error={passwordForm.errors.new_password}
              />
              <Progress
                value={Math.min(
                  passwordForm.values.new_password.length * 10,
                  100
                )}
                color={
                  passwordForm.values.new_password.length < 6
                    ? "red"
                    : passwordForm.values.new_password.length < 10
                    ? "yellow"
                    : "green"
                }
                size="sm"
                radius="xl"
              />
              <Text size="xs" c="dimmed">
                {passwordForm.values.new_password.length < 6
                  ? "Too short"
                  : passwordForm.values.new_password.length < 10
                  ? "Fair"
                  : "Strong"}
              </Text>
            </Stack>

            {/* Confirm password */}
            <PasswordInput
              label="Confirm New Password"
              placeholder="Confirm new password"
              {...passwordForm.getInputProps("confirm_password")}
              rightSection={
                passwordForm.values.confirm_password &&
                passwordForm.values.confirm_password ===
                  passwordForm.values.new_password ? (
                  <IconCheck size="1rem" color="green" />
                ) : null
              }
              error={passwordForm.errors.confirm_password}
            />

            {/* Buttons */}
            <Group justify="flex-end" mt="md">
              <Button
                variant="outline"
                onClick={() => setChangePasswordModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" loading={loading}>
                Change Password
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Two-Factor Setup Modal */}
      <Modal
        opened={twoFactorModal}
        onClose={() => setTwoFactorModal(false)}
        title="Enable Two-Factor Authentication"
        size="md"
      >
        {twoFactorSetup && (
          <Stack spacing="md">
            <Text size="sm">
              Scan this QR code with your authenticator app (Google
              Authenticator, Authy, etc.)
            </Text>

            <div style={{ textAlign: "center" }}>
              <Image
                src={twoFactorSetup.qrCode}
                alt="QR Code"
                width={200}
                height={200}
              />
            </div>

            <Text size="sm">Or enter this key manually:</Text>
            <Group>
              <Code style={{ flex: 1 }}>{twoFactorSetup.secret}</Code>
              <CopyButton value={twoFactorSetup.secret}>
                {({ copied, copy }) => (
                  <ActionIcon color={copied ? "teal" : "gray"} onClick={copy}>
                    {copied ? (
                      <IconCheck size="1rem" />
                    ) : (
                      <IconCopy size="1rem" />
                    )}
                  </ActionIcon>
                )}
              </CopyButton>
            </Group>

            <form onSubmit={twoFactorForm.onSubmit(handleEnable2FA)}>
              <TextInput
                label="Verification Code"
                placeholder="Enter 6-digit code from your app"
                {...twoFactorForm.getInputProps("token")}
                maxLength={6}
              />
              <Group justify="flex-end" mt="md">
                <Button
                  variant="outline"
                  onClick={() => setTwoFactorModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" loading={loading}>
                  Enable 2FA
                </Button>
              </Group>
            </form>
          </Stack>
        )}
      </Modal>

      {/* Phone Verification Modal */}
      <Modal
        opened={phoneVerificationModal}
        onClose={() => setPhoneVerificationModal(false)}
        title="Verify Phone Number"
      >
        <form onSubmit={phoneVerificationForm.onSubmit(handleVerifyPhone)}>
          <Stack spacing="md">
            <Text size="sm">
              Enter the 6-digit code sent to your phone number.
            </Text>
            <TextInput
              label="Verification Code"
              placeholder="Enter 6-digit code"
              {...phoneVerificationForm.getInputProps("code")}
              maxLength={6}
            />
            <Group justify="flex-end">
              <Button
                variant="outline"
                onClick={() => setPhoneVerificationModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" loading={loading}>
                Verify Phone
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Container>
  );
};

export default UserProfileScreen;
